import { motion } from 'framer-motion';
import { MapPin, Clock, Siren, PhoneCall, Navigation } from 'lucide-react';
import { KRAFT } from '@/constants/testIds';

const HOURS = [
  { day: 'Poniedziałek', range: '07:00 — 18:00', highlight: false },
  { day: 'Wtorek', range: '07:00 — 18:00', highlight: false },
  { day: 'Środa', range: '07:00 — 18:00', highlight: false },
  { day: 'Czwartek', range: '07:00 — 18:00', highlight: false },
  { day: 'Piątek', range: '07:00 — 18:00', highlight: false },
  { day: 'Sobota', range: '07:00 — 14:00', highlight: false },
  { day: 'Niedziela', range: 'Zamknięte', highlight: true },
];

export default function BusinessHours() {
  return (
    <section
      id="hours"
      data-testid={KRAFT.hoursRoot}
      className="relative bg-ink-950 py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-volt" />
          <span className="eyebrow">Godziny i lokalizacja</span>
        </div>
        <h2 className="font-display uppercase text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] max-w-4xl">
          Standardowe wizyty — <br className="hidden md:block" />
          <span className="text-volt italic font-normal">awarie o każdej porze.</span>
        </h2>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10">
          {/* Hours */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 bg-ink-950 p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <Clock className="w-5 h-5 text-volt" strokeWidth={2} />
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">Godziny standardowe</div>
            </div>
            <ul className="divide-y divide-white/10">
              {HOURS.map((h) => (
                <li key={h.day} className="flex items-center justify-between py-4">
                  <span className="font-display uppercase tracking-tight text-white text-lg md:text-xl">{h.day}</span>
                  <span
                    className={`font-mono text-sm md:text-base ${
                      h.highlight ? 'text-red-400' : 'text-slate-200'
                    }`}
                  >
                    {h.range}
                  </span>
                </li>
              ))}
            </ul>

            {/* 24/7 Emergency callout */}
            <motion.div
              data-testid={KRAFT.hoursEmergencyNote}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mt-8 relative border border-volt/60 bg-ink-900 p-6 md:p-8 overflow-hidden"
            >
              <div className="absolute -right-6 -top-6 w-32 h-32 border border-volt/20 rotate-45 pointer-events-none" />
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="w-14 h-14 border border-volt bg-volt/10 flex items-center justify-center shrink-0">
                  <Siren className="w-6 h-6 text-volt" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-volt mb-1">
                    Poza godzinami standardowymi
                  </div>
                  <div className="font-display uppercase text-white text-2xl md:text-3xl tracking-tight leading-tight">
                    Pogotowie Elektryczne <span className="text-volt">24/7</span>
                  </div>
                  <p className="text-slate-300 text-sm mt-2 max-w-md">
                    Wyłączenia zasilania, iskrzenia, zapach spalenizny, uszkodzenia rozdzielni — dyżur telefoniczny 
                    bez względu na dzień i godzinę.
                  </p>
                </div>
                <a
                  href="tel:+48731842736"
                  className="btn-volt whitespace-nowrap"
                >
                  <PhoneCall className="w-4 h-4" />
                  Zadzwoń teraz
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Location card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 bg-ink-950 p-8 md:p-12 flex flex-col"
          >
            <div className="flex items-center gap-3 mb-8">
              <MapPin className="w-5 h-5 text-volt" strokeWidth={2} />
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">Biuro · Warszawa</div>
            </div>

            <div className="font-display uppercase text-white text-3xl md:text-4xl tracking-tight leading-tight">
              Wał Miedzeszyński 466U
            </div>
            <div className="font-display uppercase text-white/70 text-2xl md:text-3xl tracking-tight leading-tight mt-1">
              03-994 Warszawa
            </div>

            <div className="mt-8 space-y-3 pb-8 border-b border-white/10">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">Dzielnica</span>
                <span className="text-slate-200 text-sm">Praga-Południe · Wawer</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">Zasięg</span>
                <span className="text-slate-200 text-sm">Warszawa i okolice · 40 km</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">Reakcja awaria</span>
                <span className="text-volt font-mono text-sm">&lt; 60 min</span>
              </div>
            </div>

            <a
              data-testid={KRAFT.hoursMapLink}
              href="https://www.google.com/maps/search/?api=1&query=Wa%C5%82+Miedzeszy%C5%84ski+466U%2C+03-994+Warszawa"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline mt-8 self-start"
            >
              <Navigation className="w-4 h-4" />
              Otwórz w Google Maps
            </a>

            {/* mini map placeholder */}
            <div className="mt-8 relative aspect-[16/10] w-full border border-white/10 overflow-hidden">
              <iframe
                title="Kraft-Performance mapa"
                src="https://www.google.com/maps?q=Wa%C5%82+Miedzeszy%C5%84ski+466U%2C+03-994+Warszawa&output=embed"
                className="absolute inset-0 w-full h-full grayscale contrast-125 brightness-75"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute inset-0 pointer-events-none mix-blend-multiply bg-ink-950/40" />
              <div className="absolute bottom-3 left-3 px-2 py-1 bg-ink-950/80 border border-volt/40">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-volt">Kraft·Performance</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
